import { useMutation as useApolloMutation } from "@apollo/client";
import { Box, Step, StepLabel, Stepper, Typography } from "@mui/material";
import React, { useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  CREATE_ALLOWANCE_PLAN,
  CreateAllowancePlanRequestInput,
  UPDATE_ALLOWANCE_PLAN,
  UpdateAllowancePlanRequestInput
} from "../../../graphql/operations";
import { useModulePlan } from "../../../hooks/useModulePlan";
import { EditMode, MembershipType, Plan } from "../../../models";
import { EmployerModuleType } from "../../../models/accounts/employerModuleType";
import { useData } from "../../../utils/useData";
import { NavBackButton } from "../../employees/NavBackButton";
import { ModulePlanFormBase } from "../ModulePlanFormBase";
import { PlanFormValues } from "../PlanFormValues";
import { ModuleRenewalOptionsFields } from "../Standard/ModuleRenewalOptionsFields";
import { AllowancesRestrictionsForm } from "./AllowancesRestrictionsForm";
import { AllowancesSettingsFields } from "./AllowancesSettingsFields";
import { AllowancesTiersFields } from "./AllowancesTiersFields";

interface StepContentProps {
  step: number;
}

export const AllowancesForm = ({
  plan,
  onClose
}: {
  plan?: Plan;
  onClose: () => void;
}) => {
  const { plans, employer, serviceSectors, accounts, membership } = useData();

  const [step, setStep] = useState<number>(0);
  const steps = ["Settings", "Tiers", "Restrictions"];
  const editMode = plan == null ? EditMode.CREATE : EditMode.EDIT;

  const isPilot = membership.data?.type === MembershipType.PILOT;
  const [error, setError] = useState<string | null>(null);

  const { initialValues, validationSchema } = useModulePlan(
    EmployerModuleType.ALLOWANCES,
    plan
  );

  const [createAllowancesPlan, { loading: createAllowancesLoading }] =
    useApolloMutation<boolean, CreateAllowancePlanRequestInput>(
      CREATE_ALLOWANCE_PLAN,
      {
        onCompleted: () => {
          toast.success("Created successfully");
          plans.refetch();
          onClose();
        },
        onError: (error) => {
          console.error(error.message);
          setError("An unexpected error occurred, please contact support.");
        }
      }
    );

  const [updateAllowancesPlan, { loading: updateAllowancesLoading }] =
    useApolloMutation<boolean, UpdateAllowancePlanRequestInput>(
      UPDATE_ALLOWANCE_PLAN,
      {
        onCompleted: () => {
          toast.success("Updated successfully");
          plans.refetch();
          onClose();
        },
        onError: (error) => {
          console.error(error.message);
          setError("An unexpected error occurred, please contact support.");
        }
      }
    );

  const handleAllowanceSave = async (values: PlanFormValues) => {
    if (!plan) {
      await createAllowancesPlan({
        variables: {
          employerId: employer.data!.id,
          request: {
            name: values.name,
            type: values.type,
            tiers: values.tiers.map((tier) => {
              return {
                id: tier.id,
                name: tier.name,
                amount: tier.amount.amount
              };
            }),
            renewalOptions: values.renewalOptions,
            serviceSectorIds: values.serviceSectorIds
          }
        }
      });
    } else {
      await updateAllowancesPlan({
        variables: {
          id: plan.id,
          employerId: employer.data!.id,
          request: {
            name: values.name,
            tiers: values.tiers.map((tier) => {
              return {
                id: tier.id,
                name: tier.name,
                amount: tier.amount.amount
              };
            }),
            serviceSectorIds: values.serviceSectorIds
          }
        }
      });
    }
  };

  const getStepValidationSchema = async (
    currentStep: number
  ): Promise<Yup.ObjectSchema<any>> => {
    switch (currentStep) {
      case 0:
        return validationSchema.pick(["name", "type", "renewalOptions"]);
      case 1:
        return validationSchema.pick(["tiers"]);
      case 2:
        return validationSchema.pick(["serviceSectorIds"]);
      default:
        return Yup.object().shape({});
    }
  };

  const handleNext = () => {
    setStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const StepContent: React.FC<StepContentProps> = ({ step }) => {
    switch (step) {
      case 0:
        return (
          <>
            <AllowancesSettingsFields />
            <ModuleRenewalOptionsFields isPilot={isPilot} editMode={editMode} />
          </>
        );
      case 1:
        return (
          <AllowancesTiersFields
            isPilot={isPilot}
            accounts={accounts.data!.items}
            editMode={editMode}
          />
        );
      case 2:
        return (
          <AllowancesRestrictionsForm
            serviceSectors={serviceSectors.data!.items}
            editMode={editMode}
          />
        );
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  return (
    <ModulePlanFormBase
      initialValues={initialValues}
      validationSchema={validationSchema}
      isLoading={createAllowancesLoading || updateAllowancesLoading}
      onClose={onClose}
      onSave={handleAllowanceSave}
      page={step}
      totalPages={steps.length}
      handleNext={handleNext}
      getStepValidationSchema={getStepValidationSchema}
    >
      <Box sx={{ width: "100%" }}>
        {step > 0 && <NavBackButton onBackPress={handleBack} />}
        <Stepper
          activeStep={step}
          alternativeLabel
          sx={{ marginBottom: 4, marginTop: 2 }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ mt: 3 }}>
          <StepContent step={step} />
        </Box>
      </Box>
      {error && <Typography color="error">{error}</Typography>}
    </ModulePlanFormBase>
  );
};
